import React, { useMemo, useState } from "react";
import styled from "styled-components";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import toast from "react-hot-toast";
import { colors } from "../../../Shared/colors";
import { Loader } from "../../../Shared/Loader";
import { filterActiveStaff } from "../../../Shared/staffStatus";
import {
  useGetRemovalImpactQuery,
  useRemoveEmployeeMutation,
} from "../../../features/employeeSlice";
import {
  Step, StepHead, Progress, CountGrid, CountCard, StaysNote, Callout,
  PickList, PickRow, Segmented, SegmentOption, Field, Foot,
  GhostBtn, NextBtn, DangerBtn, DANGER,
} from "./removeEmployeeStyles";

/**
 * Removing an employee, as a guided flow.
 *
 * The operation is irreversible and touches thirteen collections, so the design
 * priority is that the CEO understands what will happen before it happens —
 * not that it takes few clicks. Every step is reversible until the last one.
 *
 * The server decides between deleting the record outright and reassigning the
 * work then deactivating; this dialog reflects that decision rather than making
 * it, so the two can never disagree.
 */

/** Human labels for the API's movable counts, in the order they matter. */
const MOVABLE_LABELS = [
  ["clients", "Clients"],
  ["openLeads", "Open leads"],
  ["scheduledTasks", "Scheduled tasks"],
  ["sharedLeads", "Shared leads"],
  ["telesales", "Telesales assignments"],
  ["telesalesTasks", "Telesales tasks"],
  ["subordinates", "Team members"],
  ["affiliates", "Affiliates"],
];

const FROZEN_LABELS = [
  ["payments", "Commission payments"],
  ["targets", "Targets"],
  ["completedTasks", "Completed activity"],
  ["closedLeads", "Closed leads"],
  ["cmp", "Monthly plans"],
];

const nonZero = (counts = {}, labels) =>
  labels
    .map(([key, label]) => [label, counts[key] || 0])
    .filter(([, n]) => n > 0);


/**
 * Turns a failed impact lookup into something the CEO can act on.
 *
 * Two problems this fixes. The old text read only `error.data.message`, but the
 * API's error middleware nests it as `error.data.error.message` — so every real
 * failure (403 from the CEO-only guard, 404 for a missing employee) fell
 * through to "Try again in a moment." for a condition that retrying cannot fix.
 *
 * The one shape that *does* carry a top-level `message` is Express's catch-all
 * for an unmatched route, which is why a raw internal path
 * ("Can't find /api/v1/employee/…/removal-impact on the server!") reached the
 * screen. A 404 on this endpoint means the server is running a build without
 * it, which is an operator problem, not something to echo at the user.
 */
const describeImpactError = (error) => {
  const status = error?.status ?? error?.originalStatus;

  if (status === 404) {
    return "This feature isn't available on the server yet. It needs the latest backend deployed — nothing has been changed.";
  }
  if (status === 403) {
    return "Only a CEO can remove staff. Nothing has been changed.";
  }
  if (status === 401) {
    return "Your session has expired. Sign in again and retry.";
  }

  const message =
    error?.data?.error?.message || error?.data?.message || null;

  return message
    ? `Couldn't check what this employee owns. ${message}`
    : "Couldn't check what this employee owns. Try again in a moment.";
};

export const RemoveEmployeeDialog = ({ open, employee, staff = [], onClose }) => {
  const staffId = employee?._id;

  const { data, isFetching, error } = useGetRemovalImpactQuery(staffId, {
    skip: !open || !staffId,
  });
  const [removeEmployee, { isLoading: isRemoving }] = useRemoveEmployeeMutation();

  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState("spread");
  const [newManagerId, setNewManagerId] = useState("");
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");

  const impact = data?.impact;
  const fullName = `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim();

  // Anyone who could receive work: active, not the person leaving, and not one
  // of the accounts the assignment logic already excludes.
  //
  // "Active" comes from the shared derivation, which also rules out people who
  // have already left. Checking isBlock alone happened to cover that — removal
  // sets both flags — but only by coincidence, and it read as if leavers were
  // still eligible.
  const eligible = useMemo(
    () =>
      filterActiveStaff(staff).filter(
        (s) =>
          s?._id !== staffId &&
          !["developer", "v3", "ts"].includes(s?.role)
      ),
    [staff, staffId]
  );

  const needsRecipients = (impact?.totalNeedingRecipient || 0) > 0;
  const canHardDelete = Boolean(impact?.canHardDelete);

  // A hard delete has nothing to reassign, so the flow collapses to confirm.
  const steps = canHardDelete
    ? ["What happens", "Confirm"]
    : ["What happens", "Who takes over", "How to share", "Confirm"];

  const reset = () => {
    setStep(0); setPicked([]); setMode("spread");
    setNewManagerId(""); setReason(""); setTyped("");
  };

  const close = () => { reset(); onClose(); };

  const toggle = (id) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const submit = async () => {
    try {
      const result = await removeEmployee({
        staffId,
        recipients: picked.map((id) => ({ _id: id })),
        mode,
        newManagerId: newManagerId || null,
        reason: reason.trim(),
      }).unwrap();

      toast.success(
        result?.result?.mode === "deleted"
          ? `${fullName} was deleted. The account had never been used.`
          : `${fullName} removed. Their open work has been reassigned.`
      );
      close();
    } catch (err) {
      // The server's guard rails are the caller's mistake, not a crash — show
      // exactly what it said so the CEO can act on it.
      toast.error(
        err?.data?.message ||
          err?.data?.error?.message ||
          "Removal failed. Nothing was changed."
      );
    }
  };

  const canAdvance = () => {
    if (step === 0) return true;
    if (canHardDelete) return typed.trim() === fullName;
    if (step === 1) return !needsRecipients || picked.length > 0;
    if (step === 2) return true;
    return typed.trim() === fullName;
  };

  // Clamp: `steps` changes length when canHardDelete flips on a refetch, and a
  // stale index renders "Step 4 of 2" and strands the user on Continue.
  const safeStep = Math.min(step, steps.length - 1);
  const isLast = safeStep === steps.length - 1;

  return (
    <Dialog
      open={open}
      onClose={isRemoving ? undefined : close}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <Head>
        <div>
          <h2>Remove {fullName || "employee"}</h2>
          <span>{employee?.role?.toUpperCase()}</span>
        </div>
        <IconButton onClick={close} disabled={isRemoving} size="small" aria-label="Close">
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Head>

      <Progress>
        <span className="label">
          Step {safeStep + 1} of {steps.length}
        </span>
        <span className="bar">
          <span
            className="fill"
            style={{ width: `${((safeStep + 1) / steps.length) * 100}%` }}
          />
        </span>
        <span className="label">{steps[safeStep]}</span>
      </Progress>

      {isFetching ? (
        <Step><Loader /></Step>
      ) : error ? (
        <Step>
          <Callout $tone="danger">
            <WarningAmberOutlinedIcon />
            <span>{describeImpactError(error)}</span>
          </Callout>
        </Step>
      ) : (
        <>
          {step === 0 && (
            <Step>
              <StepHead>
                <h3>What happens to their work</h3>
                <p>
                  Checked against the system just now. Nothing has changed yet.
                </p>
              </StepHead>

              {canHardDelete ? (
                <Callout $tone="danger">
                  <WarningAmberOutlinedIcon />
                  <span>
                    This account has never been used — no clients, leads, tasks or
                    payments. It will be <strong>permanently deleted</strong> rather
                    than deactivated.
                  </span>
                </Callout>
              ) : (
                <>
                  <CountGrid>
                    <CountCard $stripe={colors.brown}>
                      <div className="head">
                        <span className="k">Moves to your team</span>
                        <span className="n">{impact?.totalNeedingRecipient ?? 0}</span>
                      </div>
                      <ul>
                        {nonZero(impact?.movable, MOVABLE_LABELS).map(([l, n]) => (
                          <li key={l}><span>{l}</span><span>{n}</span></li>
                        ))}
                      </ul>
                    </CountCard>

                    <CountCard $stripe={colors.textLight} $figure={colors.textMed}>
                      <div className="head">
                        <span className="k">Stays with them</span>
                        <span className="n">{impact?.totalFrozen ?? 0}</span>
                      </div>
                      <ul>
                        {nonZero(impact?.frozen, FROZEN_LABELS).map(([l, n]) => (
                          <li key={l}><span>{l}</span><span>{n}</span></li>
                        ))}
                      </ul>
                    </CountCard>
                  </CountGrid>

                  <StaysNote>
                    <strong>Commission, targets and completed activity stay with{" "}
                    {employee?.firstName || "them"} permanently.</strong> Past reports
                    will still show their name and their numbers. Only open work moves.
                  </StaysNote>
                </>
              )}
            </Step>
          )}

          {step === 1 && !canHardDelete && (
            <Step>
              <StepHead>
                <h3>Who takes over</h3>
                <p>
                  Tick the people who should receive this work. Each client keeps all
                  of their leads with one person, so nobody phones the same customer
                  twice.
                </p>
              </StepHead>

              {!needsRecipients ? (
                <Callout>
                  <LockOutlinedIcon />
                  <span>
                    There is no open work to hand over. You can continue without
                    picking anyone.
                  </span>
                </Callout>
              ) : (
                <PickList>
                  {eligible.map((s) => (
                    <li key={s._id}>
                      <PickRow>
                        <input
                          type="checkbox"
                          checked={picked.includes(s._id)}
                          onChange={() => toggle(s._id)}
                        />
                        <span className="who">
                          <span className="name">
                            {s.firstName} {s.lastName}
                          </span>
                          <span className="role">{s.role}</span>
                        </span>
                      </PickRow>
                    </li>
                  ))}
                </PickList>
              )}
            </Step>
          )}

          {step === 2 && !canHardDelete && (
            <Step>
              <StepHead>
                <h3>How to share it out</h3>
                <p>{picked.length} {picked.length === 1 ? "person" : "people"} selected.</p>
              </StepHead>

              <Segmented>
                <SegmentOption
                  type="button"
                  $active={mode === "spread"}
                  onClick={() => setMode("spread")}
                >
                  <span className="t">Share evenly</span>
                  <span className="d">
                    Spread across everyone you picked, lightest workload first
                  </span>
                </SegmentOption>
                <SegmentOption
                  type="button"
                  $active={mode === "single"}
                  onClick={() => setMode("single")}
                >
                  <span className="t">Give to one person</span>
                  <span className="d">
                    Everything goes to the first person you picked
                  </span>
                </SegmentOption>
              </Segmented>

              {(impact?.movable?.subordinates || 0) > 0 && (
                <Field>
                  <label htmlFor="new-manager">
                    New manager for their {impact.movable.subordinates} team{" "}
                    {impact.movable.subordinates === 1 ? "member" : "members"}
                  </label>
                  <select
                    id="new-manager"
                    value={newManagerId}
                    onChange={(e) => setNewManagerId(e.target.value)}
                  >
                    <option value="">Move them up to the current manager</option>
                    {eligible.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.firstName} {s.lastName} — {s.role}
                      </option>
                    ))}
                  </select>
                  <p className="hint">
                    Leave as-is and the team reports to whoever{" "}
                    {employee?.firstName || "this employee"} reported to. You cannot
                    pick someone from within the team being moved.
                  </p>
                </Field>
              )}
            </Step>
          )}

          {isLast && (
            <Step>
              <StepHead>
                <h3>Confirm</h3>
                <p>
                  {canHardDelete
                    ? "This deletes the account permanently."
                    : "This reassigns their open work and revokes their access."}{" "}
                  It cannot be undone.
                </p>
              </StepHead>

              <Callout $tone="danger">
                <WarningAmberOutlinedIcon />
                <span>
                  {canHardDelete ? (
                    <>The record for <strong>{fullName}</strong> will be deleted.</>
                  ) : (
                    <>
                      <strong>{impact?.totalNeedingRecipient ?? 0}</strong> item
                      {(impact?.totalNeedingRecipient ?? 0) === 1 ? "" : "s"} will move
                      to <strong>{picked.length || "no"}</strong>{" "}
                      {picked.length === 1 ? "person" : "people"}, and{" "}
                      <strong>{fullName}</strong> will lose access immediately.
                    </>
                  )}
                </span>
              </Callout>

              <Field>
                <label htmlFor="reason">Reason (optional, kept on the record)</label>
                <input
                  id="reason"
                  value={reason}
                  maxLength={200}
                  placeholder="Resigned, end of contract…"
                  onChange={(e) => setReason(e.target.value)}
                />
              </Field>

              <Field>
                <label htmlFor="confirm-name">
                  Type <strong>{fullName}</strong> to confirm
                </label>
                <input
                  id="confirm-name"
                  value={typed}
                  autoComplete="off"
                  placeholder={fullName}
                  onChange={(e) => setTyped(e.target.value)}
                />
              </Field>
            </Step>
          )}
        </>
      )}

      <Foot>
        <GhostBtn
          type="button"
          onClick={step === 0 ? close : () => setStep((s) => s - 1)}
          disabled={isRemoving}
        >
          {step === 0 ? "Cancel" : "Back"}
        </GhostBtn>

        {isLast ? (
          <DangerBtn
            type="button"
            onClick={submit}
            disabled={!canAdvance() || isRemoving || isFetching || Boolean(error)}
          >
            {isRemoving
              ? "Removing…"
              : canHardDelete
              ? "Delete permanently"
              : "Reassign and remove"}
          </DangerBtn>
        ) : (
          <NextBtn
            type="button"
            onClick={() => setStep((s) => s + 1)}
            disabled={!canAdvance() || isFetching || Boolean(error)}
          >
            Continue
          </NextBtn>
        )}
      </Foot>
    </Dialog>
  );
};

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 17px 16px 13px 22px;
  border-bottom: 1px solid ${colors.inputBorderWhite};

  h2 {
    margin: 0 0 3px 0;
    font-size: 1.02rem;
    font-weight: 700;
    color: ${colors.text};
  }
  span {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    color: ${DANGER};
  }
`;
